import { defineWidgetSettings } from "@nexus/widget-sdk";
import {
  DEFAULT_COUNTRY_AUDIENCE_GROUPS,
  type CountryAudienceGroupConfig,
} from "@nexus/widget-kit";
import type { PiracyAudienceCountryConfig } from "./index";

const FIGURES = {
  showMaxViewers: { metric: "max-viewers", legacy: "showPeak" },
  showViewersDisrupted: { metric: "viewers-disrupted", legacy: "showDisrupted" },
} as const;

/**
 * Whether every column shows a detail figure. The tile's switch decides. A tile
 * from before the switches chose figures per column (`metricIds`): one showing
 * it in any column keeps it; before columns, a tile-wide `showPeak` /
 * `showDisrupted` did. Otherwise it shows.
 */
export function figureShown(
  config: PiracyAudienceCountryConfig,
  key: keyof typeof FIGURES,
): boolean {
  const own = config[key];
  if (typeof own === "boolean") return own;
  const chosen = config.groups?.filter((group) => Array.isArray(group.metricIds));
  if (chosen?.length) return chosen.some((group) => group.metricIds!.includes(FIGURES[key].metric));
  const legacy = config[FIGURES[key].legacy];
  return typeof legacy === "boolean" ? legacy : true;
}

/** The tile's columns: the author's, or IPTV then Web. */
export function countryGroups(config: PiracyAudienceCountryConfig): CountryAudienceGroupConfig[] {
  return config.groups?.length ? config.groups : DEFAULT_COUNTRY_AUDIENCE_GROUPS;
}

/**
 * Which country the tile shows is not a setting here: it is the Countries filter
 * of its data settings, the same country filter as the other audience widgets.
 * Its columns (a name and the platforms each adds up) are in the data settings
 * too; what every column shows is a handful of switches, the same for all.
 */
export const settings = defineWidgetSettings({
  id: "piracy-audience-country",
  properties: {
    coalesceRequests: {
      type: "boolean",
      title: "Group compatible requests",
      description:
        "Group this tile's country selection with compatible requests. Off by default; identical requests still share their cache.",
      default: false,
      "x-ui": { category: "advanced", order: 20 },
    },
    groups: {
      type: "array",
      title: "Columns",
      description: "One column per platform group, in this order: its name and its platforms.",
      minItems: 1,
      maxItems: 16,
      items: {
        type: "object",
        required: ["id", "label", "platforms"],
        properties: {
          id: { type: "string" },
          label: { type: "string" },
          platforms: { type: "array", items: { type: "string" } },
          metricIds: { type: "array", items: { type: "string" } },
        },
      },
      "x-ui": {
        category: "data",
        control: "platform-groups",
        order: 20,
        keepDefault: true,
        options: { from: "data", request: "country", path: "groups" },
      },
    },
    showTrend: {
      type: "boolean",
      title: "Trend",
      description:
        "Change of the average audience against the previous period of the same length: green when it falls, red when it rises.",
      default: true,
      "x-ui": { category: "display", group: "show", order: 20, summary: "Trend" },
    },
    showLine: {
      type: "boolean",
      title: "Audience line",
      description: "The average audience across the period, one point a day for a week.",
      default: true,
      "x-ui": { category: "display", group: "show", order: 21, summary: "Line" },
    },
    showMaxViewers: {
      type: "boolean",
      title: "Peak",
      description: "The highest audience of the period.",
      default: true,
      "x-ui": { category: "display", group: "show", order: 22, summary: "Peak" },
    },
    showViewersDisrupted: {
      type: "boolean",
      title: "Viewers disrupted",
      description:
        "Estimated viewers disrupted by blocking, for model-covered populations and platforms.",
      default: true,
      "x-ui": { category: "display", group: "show", order: 23, summary: "Disrupted" },
    },
    titleSize: {
      type: "string",
      title: "Title size",
      description: "The country flag follows the title size.",
      default: "standard",
      oneOf: [
        { const: "standard", title: "Standard" },
        { const: "medium", title: "Medium" },
        { const: "large", title: "Large" },
      ],
      "x-ui": { category: "display", order: 11 },
    },
  },
  overlay: ({ config }) => ({
    fallbacks: {
      // Tiles from before the switches show what their columns showed.
      showMaxViewers: figureShown(config as PiracyAudienceCountryConfig, "showMaxViewers"),
      showViewersDisrupted: figureShown(
        config as PiracyAudienceCountryConfig,
        "showViewersDisrupted",
      ),
      groups: countryGroups(config as PiracyAudienceCountryConfig).map((group) => ({
        id: group.id,
        label: group.label,
        platforms: [...group.platforms],
      })),
    },
  }),
});
